import React, { useState, FunctionComponent, useEffect, useCallback } from 'react';
import { Presentation } from './presentation';
import { fetchOccupations } from '../../../services/personProfiles';
import { PreferredOccupation, SuggestOccupation, SuggestOccupationDetail } from '../../../types/personShow';
import { scrollToElementById } from '../../../libs/scrollUtils';
import {
  requiresSalesDetailWithManager,
} from '../../../libs/occupationValidation';

type Props = {
  onChangedBody: (name: string, value: any) => void;
  preferredOccupations: PreferredOccupation[] | '';
  target: string;
  onRegisterValidationHandler?: (handler: () => boolean) => void;
};

export const Component: FunctionComponent<Props> = ({
  onChangedBody,
  preferredOccupations,
  target,
  onRegisterValidationHandler,
}) => {
  const [suggestOccupations, setSuggestOccupations] = useState<SuggestOccupation[]>([]);
  const [suggestOccupationDetails, setSuggestOccupationDetails] = useState<SuggestOccupationDetail[]>([]);
  const [preferredOccupationNames, setPreferredOccupationNames] = useState<string[]>([]);
  const [preferredOccupationDetailIds, setPreferredOccupationDetailIds] = useState<number[]>([]);

  useEffect(() => {
    handleFetchOccupations();
    if (preferredOccupations !== '') {
      fetchDefaultData(preferredOccupations);
    }
  }, []);

  useEffect(() => {
    if (preferredOccupations === '') {
      fetchDefaultData([]);
    }
  }, [preferredOccupations]);

  const fetchDefaultData = (occupationsData: PreferredOccupation[]) => {
    const occupations = occupationsData
      .map((item) => (!item.occupation[1] ? item.occupation[0] : null))
      .filter(Boolean) as string[];

    const details = occupationsData
      .map((item) => item.occupationDetailId)
      .filter((id): id is number => id != null);
    const uniqueOccupations = Array.from(new Set(occupations));

    setPreferredOccupationNames(uniqueOccupations);
    setPreferredOccupationDetailIds(details);

    onChangedBody('preferredOccupations', uniqueOccupations);
    onChangedBody('preferredOccupationDetails', details);
  };

  const handleFetchOccupations = async () => {
    try {
      const response = await fetchOccupations();
      setSuggestOccupations(response.suggestOccupations);
      setSuggestOccupationDetails(response.suggestOccupationDetails);
    } catch (error) {
      console.log(error);
    }
  };

  const handleClick = (type: 'parent' | 'child', value: string | number) => {
    if (type === 'parent') {
      const exists = preferredOccupationNames.includes(value as string);
      const updated = exists
        ? preferredOccupationNames.filter(name => name !== value)
        : [...preferredOccupationNames, value as string];

      if (updated.length + preferredOccupationDetailIds.length > 12) {
        alert('12職種まで選択できます');
        return;
      }
      setPreferredOccupationNames(updated);
      onChangedBody('preferredOccupations', updated);
      onChangedBody('preferredOccupationDetails', preferredOccupationDetailIds);
    } else {
      const id = value as number;
      const exists = preferredOccupationDetailIds.includes(id);
      const updated = exists
        ? preferredOccupationDetailIds.filter(detailId => detailId !== id)
        : [...preferredOccupationDetailIds, id];

      if (updated.length + preferredOccupationNames.length > 12) {
        alert('12職種まで選択できます');
        return;
      }
      setPreferredOccupationDetailIds(updated);
      onChangedBody('preferredOccupations', preferredOccupationNames);
      onChangedBody('preferredOccupationDetails', updated);
    }
  };

  const handleScrollTo = (name: string) => {
    scrollToElementById(`flexbox_suggest_${name}`);
  };

  const validateOccupations = useCallback(() => {
    if (!requiresSalesDetailWithManager(preferredOccupationDetailIds, suggestOccupationDetails)) {
      return true;
    }

    return false;
  }, [preferredOccupationDetailIds, suggestOccupationDetails]);

  useEffect(() => {
    onRegisterValidationHandler?.(validateOccupations);
  }, [onRegisterValidationHandler, validateOccupations]);

  return (
    <Presentation
      title="希望職種を選択して保存してください"
      limit={12}
      suggestOccupations={suggestOccupations}
      suggestOccupationDetails={suggestOccupationDetails}
      preferredOccupationNames={preferredOccupationNames}
      preferredOccupationDetailIds={preferredOccupationDetailIds}
      onClick={handleClick}
      handleScrollTo={handleScrollTo}
    />
  );
};
